/**
 * @file 可定制的按钮组件
 * @description 一个可重用的按钮组件，支持不同的样式变体、尺寸和交互事件
 * 基于Tailwind CSS实现样式，可用于整个应用程序的不同位置
 */

"use client"; // 声明这是一个客户端组件

import React from 'react';

/**
 * 按钮组件属性接口
 * @interface ButtonProps
 */
interface ButtonProps {
  /** 按钮内容 - 可以是文本或其他React节点 */
  children: React.ReactNode;
  /** 按钮变体类型 - 主要(蓝色)、次要(灰色)或轮廓(带边框) */
  variant?: 'primary' | 'secondary' | 'outline';
  /** 按钮尺寸 - 小、中、大 */
  size?: 'sm' | 'md' | 'lg';
  /** 点击事件处理函数 */
  onClick?: () => void;
  /** 自定义类名 - 用于覆盖或扩展默认样式 */
  className?: string;
  /** 是否禁用按钮 */
  disabled?: boolean;
}

/**
 * 按钮组件
 * @param {ButtonProps} props - 按钮组件属性
 * @returns {JSX.Element} 按钮组件
 */
const Button: React.FC<ButtonProps> = ({
  children,        // 按钮内容
  variant = 'primary', // 默认使用主要变体(蓝色)
  size = 'md',     // 默认使用中等尺寸
  onClick,         // 点击事件处理函数
  className = '',  // 额外的自定义类名
  disabled = false, // 是否禁用按钮
}) => {
  // 基础样式 - 所有按钮共享的样式属性
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50';

  // 变体样式 - 不同类型按钮的特定样式
  const variantStyles = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500',      // 主要按钮 - 蓝色背景，白色文本
    secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200 focus-visible:ring-gray-500', // 次要按钮 - 浅灰背景，深色文本
    outline: 'border border-gray-300 bg-transparent hover:bg-gray-50 focus-visible:ring-gray-500', // 轮廓按钮 - 透明背景，灰色边框
  };

  // 尺寸样式 - 不同大小按钮的特定样式
  const sizeStyles = {
    sm: 'h-8 px-3 text-xs',  // 小按钮 - 高度8px，水平内边3px，超小字体
    md: 'h-10 px-4 text-sm', // 中按钮 - 高度10px，水平内边4px，小字体
    lg: 'h-12 px-6 text-base', // 大按钮 - 高度12px，水平内边6px，基础字体
  };

  // 渲染按钮组件，合并所有样式
  return (
    <button
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

// 导出按钮组件
export default Button;
